import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */
class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position (used for sticky="always")
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Scroll handling: prevent per-tick flicker on mobile
   */
  #ticking = false;

  /**
   * Ignore tiny scroll deltas (mobile Safari jitter / address bar collapse)
   * @constant {number}
   */
  #scrollDeltaThreshold = 8;

  /**
   * Don't engage scroll-up sticky until user is past this offset
   * @constant {number}
   */
  #revealOffset = 60;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky (ONLY for sticky="always")
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        // We intentionally do NOT use this branch for scroll-up mode in this file revision
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * Scroll handler (rAF throttled + delta deadzone to prevent mobile flicker)
   */
  #handleWindowScroll = () => {
    if (this.#ticking) return;

    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;

      const stickyMode = this.getAttribute('sticky');
      if (!stickyMode) return;

      const scrollTop = document.scrollingElement?.scrollTop ?? 0;

      // Compute delta
      const delta = scrollTop - this.#lastScrollTop;

      // Always keep lastScrollTop updated, but ignore tiny deltas that cause flicker on mobile
      if (Math.abs(delta) < this.#scrollDeltaThreshold) {
        this.#lastScrollTop = scrollTop;
        return;
      }

      const isScrollingUp = delta < 0;

      // Clear any pending hide timeout
      if (this.#timeout) {
        clearTimeout(this.#timeout);
        this.#timeout = null;
      }

      // sticky="always" (keep original behavior)
      if (stickyMode === 'always') {
        const isAtTop = this.getBoundingClientRect().top >= 0;

        if (isAtTop) {
          this.dataset.scrollDirection = 'none';
        } else if (isScrollingUp) {
          this.dataset.scrollDirection = 'up';
        } else {
          this.dataset.scrollDirection = 'down';
        }

        this.#lastScrollTop = scrollTop;
        return;
      }

      // sticky="scroll-up"
      // Only engage scroll-up sticky logic once user has scrolled a bit
      const engaged = scrollTop > this.#revealOffset;

      if (!engaged) {
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
        this.removeAttribute('data-animating');
        this.#lastScrollTop = scrollTop;
        return;
      }

      // Engage once past offset (prevents early jitter causing hide/show)
      this.#offscreen = true;

      if (isScrollingUp) {
        // Show sticky header when scrolling up
        this.removeAttribute('data-animating');

        // If the header is back at its natural position, reset state
        if (this.getBoundingClientRect().top >= 0) {
          this.#offscreen = false;
          this.dataset.stickyState = 'inactive';
          this.dataset.scrollDirection = 'none';
        } else {
          this.dataset.stickyState = 'active';
          this.dataset.scrollDirection = 'up';
        }
      } else {
        // Scrolling down: hide it
        if (this.dataset.stickyState === 'active') {
          this.dataset.scrollDirection = 'none';
          this.setAttribute('data-animating', '');

          this.#timeout = window.setTimeout(() => {
            this.dataset.stickyState = 'idle';
            this.removeAttribute('data-animating');
          }, this.#animationDelay);
        } else {
          this.dataset.scrollDirection = 'none';
          this.dataset.stickyState = 'idle';
        }
      }

      this.#lastScrollTop = scrollTop;
    });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    // Initialize scroll position to avoid first-scroll flicker
    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      // Only observe sticky position for "always" mode (theme-color + state)
      if (stickyMode === 'always') {
        this.#observeStickyPosition(true);
      }

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const updatedChildren = headerGroup.children;
          for (let i = 0; i < updatedChildren.length; i++) {
            const element = updatedChildren[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
